import {
  faBox,
  faBoxes,
  faTicketAlt,
  faTimes,
  faTruck,
} from '@fortawesome/free-solid-svg-icons';
import propTypes from 'prop-types';
import { useContext } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import {
  CLOSE,
  DELIVERED,
  DISPATCHED,
  DOMESTIC_TRACKING_NOTES,
  INTERNATIONAL_TRACKING_NOTES,
  PROCESSING_ORDER,
  READY_TO_REDEEM,
  REDEEMED,
  TRACKING_REF_IS,
  TRACKING_STEP_FOUR_TEXT,
  TRACKING_STEP_ONE_TEXT,
  TRACKING_STEP_THREE_TEXT,
  TRACKING_STEP_TWO_TEXT,
  TRACK_MY_ORDER,
} from '../../config/text/text';
import {
  getShowTrackingReference,
  getProgressStepFromVoucher,
  getShowTrackingButton,
} from '../../helpers/vouchers';
import ThemeContext from '../../providers/ThemeProvider';
import { useRedeemInstructions } from '../../services/vouchers';
import Icon from '../Icon';
import CTAButton from '../_generic/button/CTAButton';
import VoucherInstructions from './VoucherInstructions';
import VoucherProgressBar from './VoucherProgressBar';
import VoucherStep from './VoucherStep';
import { DEFAULT_EVENT_KEY, PROGRESS_EVENT_KEY } from './constants';

const VoucherAccordionProgress = ({ voucher, setToggle, toggleState }) => {
  const theme = useContext(ThemeContext);

  const steps = voucher.aftershipTrackable ? 4 : 3;
  const step = getProgressStepFromVoucher(voucher);

  const showTrackingButton = getShowTrackingButton(voucher);
  const showTrackingReference = getShowTrackingReference(voucher);
  const instructions = useRedeemInstructions({
    active: toggleState === PROGRESS_EVENT_KEY && step === 0,
    toggleState,
    voucher,
  });

  return (
    <>
      <Accordion.Collapse eventKey={PROGRESS_EVENT_KEY}>
        <div className="progress-wrapper">
          <div className="progress-header">
            <button
              aria-label={CLOSE}
              className="progress-close"
              onClick={() => setToggle(DEFAULT_EVENT_KEY)}
              type="button"
            >
              <Icon faIcon={faTimes} />
            </button>
          </div>

          <VoucherProgressBar step={step} steps={steps} />

          <div className="progress-body">
            {step === 0 && (
              <VoucherInstructions
                instructions={instructions}
                title={READY_TO_REDEEM}
              />
            )}
            {step === 1 && (
              <VoucherStep
                icon={faTicketAlt}
                iconTransform={{ rotate: -45 }}
                title={REDEEMED}
              >
                <p>{TRACKING_STEP_ONE_TEXT(voucher.warehouseDeal)}</p>
              </VoucherStep>
            )}
            {step === 2 && (
              <VoucherStep icon={faBoxes} title={PROCESSING_ORDER}>
                <p>
                  {TRACKING_STEP_TWO_TEXT(
                    voucher.warehouseDeal,
                    voucher.businessName,
                  )}
                </p>
              </VoucherStep>
            )}
            {step === 3 && (
              <VoucherStep icon={faTruck} title={DISPATCHED}>
                <p>
                  {TRACKING_STEP_THREE_TEXT(voucher)}
                  {showTrackingReference && (
                    <>
                      {` ${TRACKING_REF_IS} `}
                      <span className="primary">
                        {voucher.trackingReference}
                      </span>
                    </>
                  )}
                </p>
                {showTrackingButton && (
                  <>
                    <p>
                      {voucher.internationalDelivery
                        ? INTERNATIONAL_TRACKING_NOTES
                        : DOMESTIC_TRACKING_NOTES}
                    </p>
                    <a
                      href={voucher.trackingUrl}
                      rel="noreferrer noopener"
                      target="_blank"
                    >
                      <CTAButton>{TRACK_MY_ORDER}</CTAButton>
                    </a>
                  </>
                )}
              </VoucherStep>
            )}
            {step === 4 && (
              <VoucherStep icon={faBox} title={DELIVERED}>
                <p>{TRACKING_STEP_FOUR_TEXT}</p>
              </VoucherStep>
            )}
          </div>
        </div>
      </Accordion.Collapse>
      <style jsx>{`
        .progress-wrapper {
          border-top: 1px solid #eee;
          border-bottom: 1px solid #eee;
          padding-bottom: 14px;
          margin-bottom: 14px;
        }
        .progress-header {
          display: flex;
          flex-direction: row-reverse;
          padding: 10px;
        }
        .progress-body {
          padding: 20px;
          background: ${theme.colors.white};
          border: 1px solid ${theme.colors.lightboxgrayborders};
          border-radius: 5px;
          max-height: 360px;
          overflow: auto;
          margin-bottom: 0px;
        }
        .progress-close {
          background-color: transparent;
          border: none;
        }
        .primary {
          color: ${theme.colors.primary};
        }
      `}</style>
    </>
  );
};

VoucherAccordionProgress.propTypes = {
  setToggle: propTypes.func.isRequired,
  toggleState: propTypes.string.isRequired,
  voucher: propTypes.object,
};

export default VoucherAccordionProgress;
